import { Pencil } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatCurrency, formatDate } from '@/lib/formatters'
import type { LossRunRequest } from '@/models/lossRunRequest'
import { claimCoverageLabel } from '../claimForm'
import { EditClaimDialog } from '../EditClaimDialog'

function money(value: string | null): string {
  return value != null ? formatCurrency(parseFloat(value)) : '—'
}

interface LossRunClaimsTableProps {
  orgId: string
  request: LossRunRequest
  /** Off for customers: a claim Cai read is staff's to correct. */
  editable?: boolean
}

export function LossRunClaimsTable({ orgId, request, editable = true }: LossRunClaimsTableProps) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const claims = request.lossRunClaims ?? []
  const insurer = request.insuranceProviderContact.insuranceName
  const period = `${formatDate(request.dateStart)} – ${formatDate(request.dateEnd)}`
  const editing = claims.find((claim) => claim.id === editingId)

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-ink font-semibold">{insurer}</span>
        <span className="text-ink-500 text-sm">{period}</span>
      </div>
      {claims.length === 0 ? (
        <p className="text-ink-500 text-sm">No claims on this loss run.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Coverage</TableHead>
              <TableHead>Incident date</TableHead>
              <TableHead>Claims</TableHead>
              <TableHead>Paid</TableHead>
              <TableHead>Reserve</TableHead>
              {editable && <TableHead className="w-10" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {claims.map((claim) => (
              <TableRow key={claim.id}>
                <TableCell>{claimCoverageLabel(claim.coverageType)}</TableCell>
                <TableCell>{claim.incidentDate ? formatDate(claim.incidentDate) : '—'}</TableCell>
                <TableCell>{claim.claimCount ?? '—'}</TableCell>
                <TableCell>{money(claim.paidLoss)}</TableCell>
                <TableCell>{money(claim.reserve)}</TableCell>
                {editable && (
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Edit ${claimCoverageLabel(claim.coverageType)} claim`}
                      onClick={() => setEditingId(claim.id)}
                    >
                      <Pencil className="size-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {editing && (
        <EditClaimDialog
          claim={editing}
          providerName={insurer}
          coveragePeriod={period}
          orgId={orgId}
          open
          onOpenChange={(open) => {
            if (!open) setEditingId(null)
          }}
        />
      )}
    </div>
  )
}
